<template>
  <div>
    <!-- ── Resumen ─────────────────────────────────────────────── -->
    <div class="d-flex align-items-center gap-3 mb-3 flex-wrap">
      <span class="badge bg-danger fs-6">
        <span class="material-icons me-1" style="font-size:14px;vertical-align:middle">error</span>
        Errores: {{ conErrores.length }}
      </span>
      <span class="badge bg-warning text-dark fs-6">
        <span class="material-icons me-1" style="font-size:14px;vertical-align:middle">warning</span>
        Novedades: {{ conNovedades.length }}
      </span>
      <span class="badge bg-success fs-6">
        <span class="material-icons me-1" style="font-size:14px;vertical-align:middle">check_circle</span>
        Resueltos: {{ resueltos.length }}
      </span>
      <div class="ms-auto d-flex gap-2">
        <!-- Filtros -->
        <select class="form-select form-select-sm" v-model="filtro" style="width:160px">
          <option value="todos">Todos</option>
          <option value="errores">Solo errores</option>
          <option value="novedades">Solo novedades</option>
          <option value="pendientes">Pendientes</option>
          <option value="resueltos">Resueltos</option>
        </select>
        <!-- Marcar todos visibles como resueltos -->
        <button
          class="btn btn-success btn-sm d-flex align-items-center gap-1"
          @click="marcarTodosResueltos"
          :disabled="guardando || datosFiltrados.filter(r => !r.solucion).length === 0"
        >
          <span v-if="guardando" class="spinner-border spinner-border-sm"></span>
          <span v-else class="material-icons" style="font-size:16px">done_all</span>
          Marcar todos resueltos
        </button>
      </div>
    </div>

    <!-- ── Sin registros ──────────────────────────────────────── -->
    <div v-if="datosFiltrados.length === 0" class="alert alert-info text-center">
      <span class="material-icons d-block mb-1" style="font-size:32px;opacity:0.4">
        check_circle
      </span>
      No hay registros con errores o novedades.
    </div>

    <!-- ── Tabla ──────────────────────────────────────────────── -->
    <div v-else class="table-responsive">
      <table class="table table-sm table-bordered table-hover">
        <thead class="table-dark">
          <tr>
            <th style="width:40px">#</th>
            <th>Cédula</th>
            <th>Nombre</th>
            <th>Diagnósticos</th>
            <th>Fechas</th>
            <th>Errores / Novedades</th>
            <th style="width:100px" class="text-center">Resuelto</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="r in datosFiltrados"
            :key="r.idCargaPlanilla"
            :class="filaClase(r)"
          >
            <td class="text-muted small">{{ r.numero }}</td>
            <td class="small">{{ r.cedula }}</td>
            <td class="small">{{ r.nombre }}</td>
            <td class="small">
              <span
                v-for="d in diagnosticos(r)"
                :key="d"
                class="badge bg-secondary me-1"
              >{{ d }}</span>
            </td>
            <td class="small text-nowrap">
              {{ formatFecha(r.fechaDesde) }}
              <span v-if="r.fechaHasta && r.fechaHasta !== r.fechaDesde">
                → {{ formatFecha(r.fechaHasta) }}
              </span>
            </td>
            <td class="small">
              <!-- Errores -->
              <div v-if="r.detalleErrores" class="mb-1">
                <ul class="mb-0 ps-3">
                  <li
                    v-for="(msg, i) in parsearMensajes(r.detalleErrores)"
                    :key="i"
                    class="text-danger"
                    v-html="msg"
                  ></li>
                </ul>
              </div>
              <!-- Novedades -->
              <div v-if="r.detalleNovedades">
                <ul class="mb-0 ps-3">
                  <li
                    v-for="(msg, i) in parsearMensajes(r.detalleNovedades)"
                    :key="i"
                    class="text-warning"
                    v-html="msg"
                  ></li>
                </ul>
              </div>
            </td>
            <td class="text-center">
              <div class="form-check form-switch d-flex justify-content-center">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :checked="!!r.solucion"
                  :disabled="guardandoId === r.idCargaPlanilla"
                  @change="toggleSolucion(r)"
                  style="cursor:pointer; width:2.5em; height:1.3em"
                />
              </div>
              <small v-if="guardandoId === r.idCargaPlanilla" class="text-muted">
                <span class="spinner-border spinner-border-sm" style="width:10px;height:10px"></span>
              </small>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="table-secondary">
            <td colspan="7" class="text-end small fw-bold">
              Mostrando {{ datosFiltrados.length }} registros
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import api  from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "CargaPlanillaNovedades",
  props: {
    idTramite: { type: Number, required: true },
  },
  data() {
    return {
      registros:   [],
      filtro:      "pendientes",
      guardando:   false,
      guardandoId: null,
    };
  },
  computed: {
    // ── Registros con errores o novedades ─────────────────────
    conErrores() {
      return this.registros.filter(r => r.detalleErrores?.trim());
    },
    conNovedades() {
      return this.registros.filter(r =>
        !r.detalleErrores?.trim() && r.detalleNovedades?.trim());
    },
    resueltos() {
      return this.registros.filter(r => r.solucion === 1);
    },
    // ── Filtrado según selector ────────────────────────────────
    datosFiltrados() {
      const todos = this.registros.filter(r =>
        r.detalleErrores?.trim() || r.detalleNovedades?.trim()
      );
      switch (this.filtro) {
        case 'errores':   return todos.filter(r => r.detalleErrores?.trim());
        case 'novedades': return todos.filter(r => r.detalleNovedades?.trim());
        case 'pendientes':return todos.filter(r => !r.solucion);
        case 'resueltos': return todos.filter(r => !!r.solucion);
        default:          return todos;
      }
    },
  },
  mounted() {
    this.cargarDatos();
  },
  methods: {
    // ── Carga ─────────────────────────────────────────────────
    async cargarDatos() {
      try {
        const resp     = await api.get(
          `/vwcargaplanillas/idtramite/${this.idTramite}`);
        this.registros = Array.isArray(resp.data) ? resp.data : [];
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: "error", title: "Error",
          text: "No se pudieron cargar los registros." });
      }
    },

    // ── Toggle solución ───────────────────────────────────────
    async toggleSolucion(registro) {
      const nuevoValor = registro.solucion ? 0 : 1;
      this.guardandoId = registro.idCargaPlanilla;
      try {
        await api.post(
          `/cargaplanillas/${registro.idCargaPlanilla}/solucion`,
          { solucion: nuevoValor }
        );
        registro.solucion = nuevoValor;
      } catch (e) {
        console.error(e);
        Swal.fire({ icon: "error", title: "Error",
          text: "No se pudo actualizar el estado." });
      } finally {
        this.guardandoId = null;
      }
    },

    // ── Marcar todos los visibles como resueltos ──────────────
    async marcarTodosResueltos() {
      const pendientes = this.datosFiltrados.filter(r => !r.solucion);
      if (!pendientes.length) return;

      const confirm = await Swal.fire({
        title:             `¿Marcar ${pendientes.length} registros como resueltos?`,
        icon:              "question",
        showCancelButton:  true,
        confirmButtonText: "Sí, marcar todos",
        cancelButtonText:  "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      this.guardando = true;
      let errores = 0;
      for (const r of pendientes) {
        try {
          await api.post(
            `/cargaplanillas/${r.idCargaPlanilla}/solucion`,
            { solucion: 1 }
          );
          r.solucion = 1;
        } catch {
          errores++;
        }
      }
      this.guardando = false;
      Swal.fire({
        icon:  errores > 0 ? "warning" : "success",
        title: errores > 0
          ? `${pendientes.length - errores} marcados, ${errores} con error`
          : "Todos marcados como resueltos",
      });
    },

    // ── Helpers ───────────────────────────────────────────────
    parsearMensajes(texto) {
      if (!texto?.trim()) return [];
      return texto.split("|")
        .map(m => m.trim())
        .filter(m => m.length > 0);
    },

    diagnosticos(r) {
      return [r.diag1, r.diag2, r.diag3, r.diag4, r.diag5]
        .filter(d => d && d !== "-");
    },

    formatFecha(fecha) {
      if (!fecha) return "-";
      return fecha.slice(0, 10);
    },

    filaClase(r) {
      if (r.solucion)            return "table-success opacity-75";
      if (r.detalleErrores?.trim()) return "table-danger";
      if (r.detalleNovedades?.trim()) return "table-warning";
      return "";
    },
  },
};
</script>